import {ComponentProps, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {Box} from '@mui/material';

import {ApplicantMatchRow} from './ApplicantMatchRow';
import {BasicTable, TableSortConfig} from '../../../../../components/BasicTable/BasicTable';
import {createBasicTableColumn} from '../../../../../components/BasicTable/createBasicTableColumn';
import {empionGreen} from '../../../../../layouts/theme/EmpionColors';
import {MatchStatus} from '../../../../../types/MatchStatus/MatchStatus';
import {Match} from '../../types/ApplicantMatchesTypes';

interface Props {
  matches: Match[];
}

type RowProps = ComponentProps<typeof ApplicantMatchRow>;

const MATCH_STATUS_SORT_RANK: Record<string, number> = {
  [MatchStatus.Hired]: 0,
  [MatchStatus.Accepted]: 1,
  [MatchStatus.Interview]: 2,
  [MatchStatus.Contacted]: 3,
  [MatchStatus.Pending]: 4,
  [MatchStatus.OnHold]: 5,
  [MatchStatus.New]: 6,
  [MatchStatus.Rejected]: 7,
};

export const ApplicantMatchesTable = ({matches}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'applicantDashboard.matchesView.tableColumns'});

  const columns = useMemo(
    () => [
      createBasicTableColumn<RowProps>('company', t('company'), {
        sortable: true,
        getSortValue: (row) => row.company?.name ?? '',
        sx: {width: '16%', minWidth: 0},
      }),
      createBasicTableColumn<RowProps>('position', t('position'), {
        sortable: true,
        getSortValue: (row) => row.companyJobPosting?.title ?? '',
        sx: {width: '15%', minWidth: 0},
      }),
      createBasicTableColumn<RowProps>('location', t('location'), {
        sortable: true,
        getSortValue: (row) => row.companyJobPosting?.locations?.[0]?.city ?? '',
        sx: {width: '12%', minWidth: 0},
      }),
      createBasicTableColumn<RowProps>('salary', t('salary'), {
        sortable: true,
        getSortValue: (row) => row.companyJobPosting?.salary?.min ?? null,
        sx: {width: '12%', minWidth: 0},
      }),
      createBasicTableColumn<RowProps>('culture_fit', t('cultureFit'), {
        sortable: true,
        getSortValue: (row) => row.culturalFitScore ?? null,
        sx: {width: '11%', minWidth: 0},
      }),
      createBasicTableColumn<RowProps>('status', t('status'), {
        sortable: true,
        getSortValue: (row) => MATCH_STATUS_SORT_RANK[row.status] ?? 99,
        sx: {width: '12%', minWidth: 0},
      }),
      createBasicTableColumn<RowProps>('matched', t('matched'), {
        sortable: true,
        getSortValue: (row) => row.createdAt ?? null,
        sx: {width: '10%', minWidth: 0},
      }),
      createBasicTableColumn('actions', '', {
        align: 'right',
        sx: {width: '12%', minWidth: 0},
      }),
    ],
    [t]
  );

  const rowData = matches.map((data) => ({id: data.resourceId, ...data}));

  const initialSortConfig: TableSortConfig = {
    columnId: 'matched',
    direction: 'desc',
  };

  return (
    <Box width='100%'>
      <BasicTable<RowProps>
        columns={columns}
        Row={ApplicantMatchRow}
        rowData={rowData}
        initialSortConfig={initialSortConfig}
        hideLoadMoreFooter
        fullWidthLayout
        headerTextColor={empionGreen[700]}
      />
    </Box>
  );
};
